const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
let loaderPromise = null

export const isGoogleRoutesConfigured = Boolean(
  GOOGLE_MAPS_KEY
  && !GOOGLE_MAPS_KEY.includes('YOUR_GOOGLE_MAPS_KEY'),
)

function loadGoogleMaps() {
  if (!isGoogleRoutesConfigured) {
    return Promise.reject(new Error('Add VITE_GOOGLE_MAPS_API_KEY to .env.local first.'))
  }

  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps)
  if (loaderPromise) return loaderPromise

  loaderPromise = new Promise((resolve, reject) => {
    const callbackName = `summitHubGoogleMapsReady_${Date.now()}`
    const script = document.createElement('script')
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error('Google Maps took too long to load.'))
    }, 20000)

    function cleanup() {
      window.clearTimeout(timeout)
      try { delete window[callbackName] } catch (_error) { window[callbackName] = undefined }
    }

    window[callbackName] = () => {
      cleanup()
      resolve(window.google.maps)
    }

    script.async = true
    script.defer = true
    script.onerror = () => {
      cleanup()
      loaderPromise = null
      reject(new Error('Google Maps could not load. Check the API key and allowed website restrictions.'))
    }
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&v=weekly&loading=async&callback=${callbackName}`
    document.head.appendChild(script)
  })

  return loaderPromise
}

function coordinateFromGooglePoint(point) {
  const value = typeof point?.toJSON === 'function' ? point.toJSON() : point
  const latitude = typeof point?.lat === 'function' ? point.lat() : value?.lat
  const longitude = typeof point?.lng === 'function' ? point.lng() : value?.lng
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return null
  return [Number(longitude), Number(latitude)]
}

export function inferGoogleTravelMode(url, fallback = 'DRIVING') {
  const value = String(url || '').toLowerCase()
  if (/travelmode=transit|[!/]3e3\b|\b(train|rail|transit)\b/.test(value)) return 'TRANSIT'
  if (/travelmode=walking|[!/]3e2\b|\bwalking\b/.test(value)) return 'WALKING'
  if (/travelmode=bicycling|travelmode=bicycle|[!/]3e1\b|\b(cycling|bicycling|bicycle)\b/.test(value)) return 'BICYCLING'
  if (/travelmode=driving|[!/]3e0\b|\bdriving\b/.test(value)) return 'DRIVING'
  return fallback
}

export function isGoogleMapsDirectionsUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return /(^|\.)google\.[a-z.]+$/.test(parsed.hostname)
      || parsed.hostname === 'maps.app.goo.gl'
      || parsed.hostname === 'goo.gl'
  } catch (_error) {
    return false
  }
}

export async function computeGoogleRoute({ from, to, travelMode = 'DRIVING', departureTime = null }) {
  await loadGoogleMaps()
  const { Route } = await window.google.maps.importLibrary('routes')

  const request = {
    origin: { lat: Number(from.latitude), lng: Number(from.longitude) },
    destination: { lat: Number(to.latitude), lng: Number(to.longitude) },
    travelMode,
    computeAlternativeRoutes: true,
    polylineQuality: 'HIGH_QUALITY',
    fields: ['path', 'distanceMeters', 'durationMillis', 'description', 'localizedValues', 'routeLabels'],
  }

  if (travelMode === 'TRANSIT') {
    request.departureTime = departureTime ? new Date(departureTime) : new Date()
    request.transitPreference = {
      allowedTransitModes: ['TRAIN', 'RAIL', 'LIGHT_RAIL', 'SUBWAY', 'BUS'],
      routingPreference: 'FEWER_TRANSFERS',
    }
  }

  const { routes } = await Route.computeRoutes(request)
  if (!routes?.length) throw new Error('Google did not return a route for these two stops.')

  return routes.map((route, index) => {
    const coordinates = (route.path || []).map(coordinateFromGooglePoint).filter(Boolean)
    if (coordinates.length < 2) throw new Error('Google returned a route without a usable path.')
    return {
      index,
      coordinates,
      distance_m: Number(route.distanceMeters || 0),
      duration_seconds: Math.round(Number(route.durationMillis || 0) / 1000),
      description: route.description || '',
      route_labels: Array.isArray(route.routeLabels) ? route.routeLabels : [],
      distance_text: route.localizedValues?.distance || '',
      duration_text: route.localizedValues?.duration || '',
    }
  })
}
